import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { kycApi } from '../lib/api';
import { formatCurrency, getKYCTierInfo } from '../lib/utils';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, Check, Lock, Shield, FileText, Camera, ChevronRight } from 'lucide-react';

const KYCPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [kycStatus, setKycStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => { fetchKYCStatus(); }, []);

  const fetchKYCStatus = async () => {
    try {
      const res = await kycApi.getStatus();
      setKycStatus(res.data);
    } catch (error) {
      toast.error('Failed to load KYC status');
    } finally {
      setIsLoading(false);
    }
  };

  const tiers = [
    { tier: 1, name: 'Basic', requirements: ['Name', 'Phone number', 'Email'], icon: Shield },
    { tier: 2, name: 'Standard', requirements: ['Government ID', 'Date of birth', 'ID verification'], icon: FileText },
    { tier: 3, name: 'Premium', requirements: ['Selfie verification', 'Proof of address', 'Source of funds'], icon: Camera },
  ];

  const currentTier = user?.kyc_tier || 1;

  return (
    <AppLayout>
      <div className="min-h-screen lg:min-h-0 pb-8">
        {/* Header */}
        <div className="p-6 lg:p-0 lg:mb-6 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/5 rounded-lg transition-colors lg:hidden"><ArrowLeft className="w-6 h-6" /></button>
          <h1 className="text-xl lg:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Verification Status</h1>
        </div>

        <div className="px-6 lg:px-0 lg:grid lg:grid-cols-2 lg:gap-8 space-y-6 lg:space-y-0">
          {/* Left Column */}
          <div className="space-y-6">
            {/* Current Tier Badge */}
            <div className="bg-card border border-white/10 rounded-2xl p-6 text-center card-accent relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-primary" />
              <div className={`w-16 h-16 mx-auto rounded-full ${getKYCTierInfo(currentTier).bgColor} flex items-center justify-center mb-4`}>
                <Shield className={`w-8 h-8 ${getKYCTierInfo(currentTier).color}`} />
              </div>
              <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Tier {currentTier} - {getKYCTierInfo(currentTier).name}</h2>
              <p className="text-muted-foreground">{currentTier < 3 ? 'Upgrade to unlock higher limits' : 'You have full access'}</p>
            </div>
            
            {/* Tier Progress */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                {[1, 2, 3].map((tier) => (
                  <div key={tier} className={`flex-1 h-2 rounded-full ${tier <= currentTier ? 'bg-primary' : 'bg-white/10'}`} />
                ))}
              </div>
              <p className="text-xs text-muted-foreground text-center">{currentTier < 3 ? `${currentTier}/3 tiers completed` : 'All tiers completed'}</p>
            </div>
            
            {/* Account Limits */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Current Limits</h3>
              <div className="bg-card border border-white/10 rounded-xl overflow-hidden">
                {[
                  { label: 'Daily Limit', value: kycStatus?.limits?.daily || 50000 },
                  { label: 'Single Transaction', value: kycStatus?.limits?.single || 20000 },
                  { label: 'Monthly Limit', value: kycStatus?.limits?.monthly || 300000 },
                ].map((limit, idx) => (
                  <div key={limit.label} className={`flex items-center justify-between p-4 ${idx < 2 ? 'border-b border-white/10' : ''}`}>
                    <span className="text-muted-foreground">{limit.label}</span><span className="font-medium">{formatCurrency(limit.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-6">
            {/* Tier Ladder */}
            <div>
              <h3 className="text-sm font-medium text-muted-foreground mb-3">Verification Tiers</h3>
              <div className="space-y-3">
                {tiers.map((tier) => {
                  const tierInfo = getKYCTierInfo(tier.tier);
                  const isCompleted = currentTier >= tier.tier;
                  const isNext = currentTier + 1 === tier.tier;
                  const Icon = tier.icon;
                  return (
                    <div key={tier.tier} className={`bg-card border rounded-xl p-4 transition-colors ${isCompleted ? 'border-green-500/30' : isNext ? 'border-primary/30' : 'border-white/10'}`}>
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-full ${tierInfo.bgColor} flex items-center justify-center`}>
                          {isCompleted ? <Check className="w-6 h-6 text-green-500" /> : <Icon className={`w-6 h-6 ${tierInfo.color}`} />}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <p className="font-medium">Tier {tier.tier} - {tier.name}</p>
                            {isCompleted && <span className="text-xs bg-green-500/20 text-green-500 px-2 py-0.5 rounded-full">Verified</span>}
                          </div>
                          <p className="text-sm text-muted-foreground">{tier.requirements.join(' | ')}</p>
                        </div>
                        {!isCompleted && <Lock className="w-5 h-5 text-muted-foreground" />}
                      </div>
                      {isNext && (
                        <Button className="w-full mt-4 bg-primary hover:bg-red-600 text-white font-semibold" onClick={() => toast.info('KYC submission coming soon')} data-testid="upgrade-tier">
                          Upgrade to Tier {tier.tier}<ChevronRight className="w-5 h-5 ml-2" />
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {currentTier < 3 && kycStatus?.next_tier_limits && (
              <div className="bg-primary/10 border border-primary/20 rounded-xl p-4">
                <p className="font-medium text-primary mb-3">Tier {currentTier + 1} Benefits</p>
                <div className="space-y-2 text-sm">
                  <p>Daily limit: {formatCurrency(kycStatus.next_tier_limits.daily)}</p>
                  <p>Single transaction: {formatCurrency(kycStatus.next_tier_limits.single)}</p>
                  <p>Monthly limit: {formatCurrency(kycStatus.next_tier_limits.monthly)}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default KYCPage;
